import React, { useState, useEffect, useRef } from "react";
import { 
  Settings, Zap, RefreshCw, Smartphone, CheckCircle, 
  Trash2, HelpCircle, FileText, Upload, Check, ExternalLink, 
  ChevronDown, X, Ban
} from "lucide-react";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

// ONLY the 2 approved Meta templates + None (Disabled)[cite: 14]
const EXACT_APPROVED_TEMPLATES = [
  {
    id: "expolead",
    name: "ExpoLead Default",
    description: "Standard business greeting with company intro, specialization, and brochure/website link."
  },
  {
    id: "post_expo_followup_v2",
    name: "Post Expo Follow-up (v2)",
    description: "Booth visitor follow-up message with quick brochure sharing and meeting greeting."
  },
  {
    id: "none",
    name: "None (Do Not Send)",
    description: "Disable automated WhatsApp follow-ups. Business cards will be scanned without sending any message."
  }
];

const ALLOWED_TEMPLATE_IDS = new Set(["expolead", "post_expo_followup_v2", "none"]);

export default function WhatsAppOnboarding() {
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetchingConfig, setFetchingConfig] = useState(true);
  const [config, setConfig] = useState(null);
  
  const [statusInfo, setStatusInfo] = useState({
    mode: "not_configured",
    phone_number_id: null,
    template_name: "expolead",
    catalog_url: null,
    catalog_filename: null
  });

  const [availableTemplates, setAvailableTemplates] = useState(EXACT_APPROVED_TEMPLATES);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [customTemplateInput, setCustomTemplateInput] = useState("");
  const [uploadingCatalog, setUploadingCatalog] = useState(false);
  const catalogInputRef = useRef(null);

  const metaSessionRef = useRef({ phone_number_id: null, waba_id: null });
  const isMountedRef = useRef(true);

  const isExpoleadSelected = statusInfo?.mode === "expolead_default";
  const isCustomSelected = statusInfo?.mode === "custom_number";
  const isConfigured = isExpoleadSelected || isCustomSelected;

  const fetchStatusAndConfig = async () => {
    try {
      setFetchingConfig(true);
      const [configRes, statusRes, templateRes] = await Promise.all([
        api.get("/whatsapp/config").catch(() => ({ data: null })),
        api.get("/whatsapp/status").catch(() => ({ data: null })),
        api.get("/whatsapp/templates").catch(() => ({ data: null }))
      ]);

      if (!isMountedRef.current) return;

      if (configRes?.data) {
        setConfig(configRes.data);
        if (configRes.data.app_id) {
          loadMetaSDK(configRes.data.app_id, configRes.data.api_version);
        }
      }

      if (statusRes?.data) {
        let activeTpl = (statusRes.data.template_name || "expolead").toLowerCase().trim();
        if (!ALLOWED_TEMPLATE_IDS.has(activeTpl)) {
          activeTpl = "expolead";
        }

        setStatusInfo((prev) => ({
          ...prev,
          ...statusRes.data,
          template_name: activeTpl
        }));
      }

      if (templateRes?.data?.templates && Array.isArray(templateRes.data.templates)) {
        const filtered = templateRes.data.templates.filter((t) => ALLOWED_TEMPLATE_IDS.has(t.id));
        setAvailableTemplates(filtered.length > 0 ? filtered : EXACT_APPROVED_TEMPLATES);
      } else {
        setAvailableTemplates(EXACT_APPROVED_TEMPLATES);
      }
    } catch (err) {
      console.error("Initialization error:", err);
    } finally {
      if (isMountedRef.current) setFetchingConfig(false);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    fetchStatusAndConfig();

    const handleMetaMessage = (event) => {
      if (
        typeof event.origin === "string" &&
        !event.origin.includes("facebook.com") &&
        !event.origin.includes("meta.com")
      ) {
        return;
      }

      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data?.type === "WA_EMBEDDED_SIGNUP") {
          if (
            data.event === "FINISH" ||
            data.event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING" ||
            data.data?.is_wa_login_user ||
            data.data?.phone_number_id
          ) {
            const { phone_number_id, waba_id } = data.data || {};
            metaSessionRef.current = {
              phone_number_id: phone_number_id || null,
              waba_id: waba_id || null,
            };
          } else if (data.event === "CANCEL") {
            toast.info("WhatsApp connection was cancelled.");
          } else if (data.event === "ERROR") {
            toast.error(data.data?.error_message || "Meta verification error.");
          }
        }
      } catch { /* ignore */ }
    };

    window.addEventListener("message", handleMetaMessage);
    return () => {
      isMountedRef.current = false;
      window.removeEventListener("message", handleMetaMessage);
    };
  }, []);

  const loadMetaSDK = (appId, apiVersion) => {
    let cleanVersion = apiVersion ? String(apiVersion).trim() : "v25.0";
    if (!cleanVersion.startsWith("v")) cleanVersion = `v${cleanVersion}`;
    if (!cleanVersion.includes(".")) cleanVersion = `${cleanVersion}.0`;

    if (window.FB) {
      window.FB.init({
        appId: appId,
        cookie: true,
        xfbml: true,
        version: cleanVersion,
      });
      return;
    }

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: appId,
        cookie: true,
        xfbml: true,
        version: cleanVersion,
      });
    };

    (function (d, s, id) {
      var js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s);
      js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      fjs.parentNode.insertBefore(js, fjs);
    })(document, "script", "facebook-jssdk");
  };

  const handleSelectDefaultNumber = async () => {
    if (isExpoleadSelected) return;
    try {
      setLoading(true);
      const res = await api.post("/whatsapp/use-default-number");

      if (!isMountedRef.current) return;

      if (res?.data) {
        setStatusInfo((prev) => ({
          ...prev,
          mode: "expolead_default",
          phone_number_id: res.data.phone_number_id,
        }));
        if (setUser && user) {
          setUser({ ...user, whatsapp_phone_number_id: res.data.phone_number_id, whatsapp_access_token: null });
        }
        toast.success("Successfully selected ExpoLead's Verified Number!");
      }
    } catch (err) {
      if (isMountedRef.current) {
        toast.error(err?.response?.data?.detail || "Failed to connect ExpoLead number.");
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const handleLaunchEmbeddedSignup = () => {
    if (!config?.config_id) {
      toast.error("META_CONFIG_ID is missing in backend configuration.");
      return;
    }

    if (!window.FB) {
      toast.info("Connecting to Meta Gateway. Please retry in 2 seconds.");
      return;
    }

    setLoading(true);

    window.FB.login(
      function (response) {
        if (!isMountedRef.current) return;

        if (response.authResponse && response.authResponse.code) {
          const authCode = response.authResponse.code;
          const { waba_id, phone_number_id } = metaSessionRef.current;

          api
            .post("/whatsapp/embedded-signup-callback", {
              code: authCode,
              waba_id: waba_id,
              phone_number_id: phone_number_id,
            })
            .then(async (res) => {
              if (!isMountedRef.current) return;
              if (res?.data) {
                const targetPhoneId = res.data.phone_number_id || phone_number_id;
                setStatusInfo((prev) => ({
                  ...prev,
                  mode: "custom_number",
                  phone_number_id: targetPhoneId,
                }));
                if (setUser && user) {
                  setUser({ ...user, whatsapp_phone_number_id: targetPhoneId });
                }
                toast.success("Custom WhatsApp number connected with sponsored billing!");
              }
            })
            .catch((err) => {
              if (isMountedRef.current) {
                toast.error(err?.response?.data?.detail || "Custom number connection failed.");
              }
            })
            .finally(() => {
              if (isMountedRef.current) setLoading(false);
            });
        } else {
          setLoading(false);
          toast.error("Meta login was cancelled.");
        }
      },
      {
        config_id: config.config_id,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: "whatsapp_business_app_onboarding",
          sessionInfoVersion: "3",
        },
      }
    );
  };

  const handleResetSelection = async () => {
    if (!window.confirm("Disconnect/reset your active WhatsApp sender?")) return;
    try {
      setLoading(true);
      await api.post("/whatsapp/disconnect");

      if (!isMountedRef.current) return;
      setStatusInfo((prev) => ({
        ...prev,
        mode: "not_configured",
        phone_number_id: null,
      }));
      if (setUser && user) {
        setUser({ ...user, whatsapp_phone_number_id: null, whatsapp_access_token: null });
      }
      toast.success("WhatsApp sender successfully reset.");
    } catch (err) {
      if (isMountedRef.current) {
        toast.error("Failed to reset sender selection.");
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const handleSelectTemplate = async (templateName) => {
    try {
      setLoading(true);
      const res = await api.post("/whatsapp/select-template", { template_name: templateName });

      if (res?.data) {
        setStatusInfo((prev) => ({ ...prev, template_name: res.data.template_name }));
        if (setUser && user) {
          setUser({ ...user, whatsapp_template_name: res.data.template_name });
        }
        if (res.data.template_name === "none") {
          toast.info("Automated WhatsApp messages disabled (Template: None).");
        } else {
          toast.success(`Template updated to '${res.data.template_name}'`);
        }
        setIsTemplateModalOpen(false);
        setCustomTemplateInput("");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to switch template.");
    } finally {
      setLoading(false);
    }
  };

  const handleCatalogUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a valid PDF document.");
      if (catalogInputRef.current) catalogInputRef.current.value = "";
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File exceeds the 2 MB limit. Please compress your PDF.");
      if (catalogInputRef.current) catalogInputRef.current.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploadingCatalog(true);
      const res = await api.post("/whatsapp/upload-catalog", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (res?.data) {
        setStatusInfo((prev) => ({
          ...prev,
          catalog_url: res.data.catalog_url,
          catalog_filename: res.data.catalog_filename
        }));
        if (setUser && user) {
          setUser({ ...user, catalog_url: res.data.catalog_url, catalog_filename: res.data.catalog_filename });
        }
        toast.success("Brochure uploaded! It will be sent automatically to scanned leads.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to upload brochure.");
    } finally {
      setUploadingCatalog(false);
      if (catalogInputRef.current) catalogInputRef.current.value = "";
    }
  };

  const handleRemoveCatalog = async () => {
    if (!window.confirm("Remove this brochure? Follow-ups will default to your website link.")) return;
    try {
      setUploadingCatalog(true);
      await api.delete("/whatsapp/remove-catalog");

      setStatusInfo((prev) => ({ ...prev, catalog_url: null, catalog_filename: null }));
      if (setUser && user) {
        setUser({ ...user, catalog_url: null, catalog_filename: null });
      }
      toast.success("Brochure removed successfully.");
    } catch (err) {
      toast.error("Failed to remove brochure.");
    } finally {
      setUploadingCatalog(false);
    }
  };

  // Safe PDF brochure opener for Capacitor Android webviews[cite: 14]
  const handleOpenPdf = async (e, url) => {
    e.preventDefault();
    if (!url) return;

    const fullUrl = url.startsWith("http")
      ? url
      : `https://expolead.thensgroup.in${url.startsWith("/") ? "" : "/"}${url}`;

    if (Capacitor.isNativePlatform()) {
      try {
        await Browser.open({ url: fullUrl });
        return;
      } catch (err) {
        console.warn("Capacitor browser open failed, falling back to window.open", err);
      }
    }

    window.open(fullUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="bg-white/80 backdrop-blur-xl border border-slate-200/80 rounded-3xl p-5 sm:p-6 shadow-sm space-y-5 max-w-2xl mx-auto font-sans">
      
      {/* 1. SENDER HEADER */}
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-3.5">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-blue-50/80 text-blue-600 border border-blue-100/60">
            <Settings className="w-3.5 h-3.5" />
          </div>
          <h2 className="text-xs font-bold tracking-wider text-slate-800 uppercase">
            WhatsApp Sender Setup
          </h2>
        </div>

        <div className="flex items-center gap-2">
          {isConfigured && (
            <button
              type="button"
              disabled={loading}
              onClick={handleResetSelection}
              className="px-2.5 py-1 bg-rose-50/70 hover:bg-rose-100/80 text-rose-700 border border-rose-200/60 text-[11px] font-semibold rounded-full transition-all flex items-center gap-1 cursor-pointer disabled:opacity-50"
              title="Deselect Sender"
            >
              {loading ? (
                <RefreshCw className="w-3 h-3 animate-spin" />
              ) : (
                <Trash2 className="w-3 h-3 text-rose-600" />
              )}
              <span>Deselect</span>
            </button>
          )}

          {isExpoleadSelected && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50/80 text-emerald-700 border border-emerald-200/60 text-[11px] font-semibold rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              ExpoLead Verified
            </span>
          )}
          {isCustomSelected && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50/80 text-blue-700 border border-blue-200/60 text-[11px] font-semibold rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
              Custom Number
            </span>
          )}
          {!isConfigured && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100/80 text-slate-600 border border-slate-200/60 text-[11px] font-semibold rounded-full">
              <HelpCircle className="w-3 h-3 text-slate-400" />
              No Sender
            </span>
          )}
        </div>
      </div>

      {/* 2. SENDER CARDS[cite: 14] */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div
          onClick={handleSelectDefaultNumber}
          className={`p-4 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between relative ${
            isExpoleadSelected
              ? "border-emerald-500/80 bg-emerald-50/40 shadow-xs ring-1 ring-emerald-500/20"
              : "border-slate-200/70 hover:border-slate-300 bg-white/60 hover:bg-white"
          }`}
        >
          {isExpoleadSelected && (
            <div className="absolute top-3.5 right-3.5 text-emerald-600">
              <CheckCircle className="w-4 h-4 fill-emerald-600 text-white" />
            </div>
          )}
          <div className="space-y-1 pr-5">
            <div className="flex items-center gap-1.5 text-slate-900 font-bold text-xs">
              <Zap className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span>Use ExpoLead's Number</span>
            </div>
            <p className="text-[11px] text-slate-600 leading-relaxed">
              Dispatches instantly from ExpoLead's verified business number.
            </p>
          </div>
          <div className="pt-2.5 border-t border-slate-100 mt-3 text-[10px] text-slate-500 font-medium space-y-0.5">
            <div>✓ Instant setup (Zero config)</div>
          </div>
        </div>

        <div
          onClick={handleLaunchEmbeddedSignup}
          className={`p-4 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between relative ${
            isCustomSelected
              ? "border-blue-500/80 bg-blue-50/40 shadow-xs ring-1 ring-blue-500/20"
              : "border-slate-200/70 hover:border-slate-300 bg-white/60 hover:bg-white"
          }`}
        >
          {isCustomSelected && (
            <div className="absolute top-3.5 right-3.5 text-blue-600">
              <CheckCircle className="w-4 h-4 fill-blue-600 text-white" />
            </div>
          )}
          <div className="space-y-1 pr-5">
            <div className="flex items-center gap-1.5 text-slate-900 font-bold text-xs">
              <Smartphone className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              <span>Setup Your Own Number</span>
            </div>
            <p className="text-[11px] text-slate-600 leading-relaxed">
              Deliver brochures from your company's official business WhatsApp number.
            </p>
          </div>
          <div className="pt-2.5 border-t border-slate-100 mt-3 text-[10px] text-slate-500 font-medium space-y-0.5">
            <div>✓ Connects via Meta Embedded Login</div>
          </div>
        </div>
      </div>

      {/* 3. PRODUCT CATALOG (MAX 2 MB PDF)[cite: 14] */}
      <div className="p-4 bg-slate-50/60 border border-slate-200/70 rounded-2xl space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 text-blue-600" />
            Product Brochure PDF
          </span>
          <span className="text-[10px] font-semibold px-2 py-0.5 bg-slate-200/60 text-slate-700 rounded-md">
            Max 2 MB (PDF Only)
          </span>
        </div>

        {statusInfo?.catalog_url ? (
          <div className="p-3 bg-white border border-slate-200/80 rounded-xl flex items-center justify-between gap-2 shadow-2xs">
            <div className="min-w-0 flex items-center gap-2">
              <div className="p-1.5 bg-emerald-50 text-emerald-700 rounded-lg shrink-0">
                <FileText className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-900 truncate">
                  {statusInfo.catalog_filename || "Product_Catalog.pdf"}
                </p>
                <button
                  type="button"
                  onClick={(e) => handleOpenPdf(e, statusInfo.catalog_url)}
                  className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1 font-medium cursor-pointer"
                >
                  <span>View PDF</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => catalogInputRef.current?.click()}
                disabled={uploadingCatalog}
                className="px-2.5 py-1 text-[11px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition cursor-pointer"
              >
                {uploadingCatalog ? "Uploading..." : "Change"}
              </button>
              <button
                type="button"
                onClick={handleRemoveCatalog}
                disabled={uploadingCatalog}
                className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg transition cursor-pointer"
                title="Delete PDF"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ) : (
          <div 
            onClick={() => catalogInputRef.current?.click()}
            className="border border-dashed border-slate-300 hover:border-blue-500/80 p-4 rounded-xl text-center cursor-pointer transition bg-white/70 hover:bg-white space-y-1"
          >
            <Upload className="w-4 h-4 text-slate-400 mx-auto" />
            <p className="text-xs font-bold text-slate-700">
              {uploadingCatalog ? "Uploading PDF..." : "Upload product brochure (PDF format)"}
            </p>
            <p className="text-[11px] text-slate-500">
              Sent automatically to visitors upon scanning their visiting card.
            </p>
          </div>
        )}

        <input 
          ref={catalogInputRef} 
          type="file" 
          accept="application/pdf" 
          className="hidden" 
          onChange={handleCatalogUpload} 
        />
      </div>

      {/* 4. TEMPLATE SELECTION BAR[cite: 14] */}
      <div className="p-4 bg-slate-50/70 border border-slate-200/70 rounded-2xl space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
            Active Follow-Up Template
          </span>
          <button
            type="button"
            onClick={() => setIsTemplateModalOpen(true)}
            className="py-1.5 px-3 bg-white hover:bg-slate-100 text-slate-800 border border-slate-200 font-semibold text-xs rounded-xl transition-all shadow-2xs flex items-center gap-1.5 cursor-pointer shrink-0"
          >
            <span>Select Template</span>
            <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
          </button>
        </div>

        <div className="flex items-center gap-2 bg-white/90 border border-slate-200/80 px-3.5 py-2.5 rounded-xl shadow-2xs min-w-0">
          {statusInfo.template_name === "none" ? (
            <div className="flex items-center gap-2 min-w-0">
              <Ban className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-xs font-semibold text-slate-500 italic truncate">
                None (Dispatches disabled)
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 min-w-0 w-full overflow-hidden">
              <div className="w-2 h-2 rounded-full bg-blue-600 shrink-0"></div>
              <span className="text-xs font-semibold text-slate-900 truncate">
                {statusInfo.template_name === "post_expo_followup_v2"
                  ? "Post Expo Follow-up (v2)"
                  : "ExpoLead Default"}
              </span>
              <span className="text-[11px] font-mono text-slate-400 truncate shrink-0">
                ({statusInfo.template_name === "post_expo_followup_v2" ? "post_expo_followup_v2" : "expolead"})
              </span>
            </div>
          )}
        </div>
      </div>

      {/* 5. TEMPLATE SELECTION MODAL[cite: 14] */}
      {isTemplateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/40 backdrop-blur-md animate-in fade-in duration-200">
          <div className="w-full max-w-md bg-white/90 backdrop-blur-xl border border-white/60 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.2)] rounded-3xl p-6 space-y-4 text-slate-900 relative">
            <div className="flex items-start justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-bold tracking-tight text-slate-900">Select Meta Template</h3>
                <p className="text-xs text-slate-500 mt-0.5">Choose your approved template or disable messages.</p>
              </div>
              <button
                type="button"
                onClick={() => setIsTemplateModalOpen(false)}
                className="p-1.5 -mr-1 -mt-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100/80 rounded-full transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-2.5">
              {availableTemplates.map((t) => {
                const isSelected = statusInfo.template_name === t.id;
                const isNone = t.id === "none";
                return (
                  <div
                    key={t.id}
                    onClick={() => handleSelectTemplate(t.id)}
                    className={`p-3.5 rounded-2xl border transition-all cursor-pointer flex items-start justify-between gap-3 ${
                      isSelected
                        ? isNone
                          ? "bg-amber-50/70 border-amber-400/80 shadow-xs ring-1 ring-amber-400/30 text-amber-950"
                          : "bg-blue-50/70 border-blue-500/80 shadow-xs ring-1 ring-blue-500/30 text-blue-950"
                        : "bg-white/60 hover:bg-white border-slate-200/70 hover:border-slate-300/90 hover:shadow-2xs text-slate-800"
                    }`}
                  >
                    <div className="space-y-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {isNone ? (
                          <Ban className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        ) : (
                          <div className={`w-2 h-2 rounded-full shrink-0 ${isSelected ? "bg-blue-600" : "bg-slate-400"}`}></div>
                        )}
                        <span className="text-xs font-bold">{t.name}</span>
                        {!isNone && (
                          <span className="font-mono text-[10px] text-slate-500">({t.id})</span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500 leading-relaxed pr-2">
                        {t.description}
                      </p>
                    </div>

                    <div className="shrink-0 mt-0.5">
                      {isSelected ? (
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-white ${isNone ? "bg-amber-600" : "bg-blue-600"}`}>
                          <Check className="w-3 h-3 stroke-[2.5]" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full border border-slate-300/80 bg-white/50"></div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="pt-3 border-t border-slate-100 space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                Or Use Another Approved Template Name
              </label>
              <div className="flex items-center gap-2 p-1 bg-slate-100/70 backdrop-blur-xs border border-slate-200/70 rounded-2xl focus-within:bg-white focus-within:border-blue-500/80 focus-within:ring-2 focus-within:ring-blue-500/10 transition-all">
                <input
                  type="text"
                  placeholder="e.g. post_expo_followup_v2"
                  value={customTemplateInput}
                  onChange={(e) => setCustomTemplateInput(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                  className="flex-1 bg-transparent px-3 py-1.5 text-xs font-mono text-slate-900 placeholder:text-slate-400 outline-none"
                />
                <button
                  type="button"
                  disabled={!customTemplateInput.trim()}
                  onClick={() => handleSelectTemplate(customTemplateInput.trim())}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-30 text-white font-semibold text-xs rounded-xl shadow-xs transition-colors cursor-pointer"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}